/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Table, Tooltip } from 'antd'
import { formatShortDate } from '@sportninja/common/ts-utils/date-utils'
import type {
  BalanceBillingSchedule,
  OfflinePaymentBatch,
} from '@sportninja/common/services/teamBalance'
import colors from '@sportninja/common/constants/appColors'
import Price from 'src/components/ts-components/Price'
import { dollarsToMinor } from '../utils/balanceMath'
import {
  balanceLabel,
  balanceText,
  billingTypeLabel,
} from '../utils/balanceI18n'
import { isPastDueDay } from '../utils/billDueDay'
import { StatusDotLabel } from './BalanceStatusChip'
import OfflineBatchRow from './OfflineBatchRow'
import InlineTextButton from 'src/components/ActionButton/InlineTextButton'
import SlotNoteEditor from './SlotNoteEditor'
import { paragraphXSmall, weightSemiBold } from 'src/components/css'
import { formatCurrency } from '@sportninja/common/currency'

/** The raised surface used by the sheet and the KPI cards. */
const panelStyle = css`
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  background: ${colors.SECONDARY_300};
  overflow: hidden;
`

/** Section heading, matching the KPI card titles. */
const sectionTitleStyle = css`
  ${weightSemiBold};
  font-size: 11px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_70};
`

type SlotExpandedRowProps = {
  billingSchedules: BalanceBillingSchedule[]
  currencyCode: string
  offlinePayments?: OfflinePaymentBatch[]
  /** Receives the batch, whose own `line_kind` the void sends (Sep 23). */
  onVoidOffline?: (batch: OfflinePaymentBatch) => void
  voidingBatchId?: string | null
  /** D39 — verification affordance, forwarded to each batch row. */
  onVerifyOffline?: (batch: OfflinePaymentBatch) => void
  verifyingBatchId?: string | null
  /**
   * A2 — the note editor. Rendered only for an OCCUPIED slot: the note is about
   * a person, and an open slot has nobody to write about. `slotId` +
   * `playerRegistrationId` key the editor so a reassignment under an open drawer
   * remounts it rather than letting one player's draft become another's.
   */
  slotId?: string
  playerRegistrationId?: string | null
  note?: string | null
  onSaveNote?: (
    note: string | null,
    expectedPlayerRegistrationId: string
  ) => Promise<unknown>
  savingNote?: boolean
  /**
   * SN-8310 — undo an added invoice. Offered only on rows the server marks
   * `can_remove` (unpaid, not being charged, not split, not in a pending offline
   * payment); the caller confirms before anything is removed.
   */
  onRemoveAddedInvoice?: (bill: BalanceBillingSchedule) => void
  removingBillUid?: string | null
  /**
   * Sep 23 (D11) — why this viewer may not remove an added invoice here (the payer of
   * a team official's own line). Remove then shows, disabled, with this as its tooltip.
   */
  removeBlockedReason?: string
}

type RowState = { label: string; dotColor: string }

/**
 * The per-row state — STATE ONLY (no receipt or pay link; the rep drives money
 * elsewhere). Refunds override paid: a refunded row's money no longer counts
 * (D29). An unpaid row past its due date reads overdue.
 */
const rowState = (row: BalanceBillingSchedule): RowState => {
  if (row.refunded) {
    return {
      label: balanceLabel('common:balanceStateRefunded'),
      dotColor: colors.WARNING_200,
    }
  }
  if (row.paid) {
    return {
      label: balanceLabel(
        row.paid_in_person
          ? 'common:balanceStatePaidInPerson'
          : 'common:balanceStatePaid'
      ),
      dotColor: colors.SUCCESS_200,
    }
  }
  if (row.due_date && isPastDueDay(row.due_date)) {
    return {
      label: balanceLabel('common:balanceStateOverdue'),
      dotColor: colors.ERROR_200,
    }
  }
  return {
    label: balanceLabel('common:balanceStateScheduled'),
    dotColor: colors.NEUTRAL_100,
  }
}

/**
 * The billing breakdown for one slot, plus the rep's note about its player.
 *
 * A2 restructured this: it used to EARLY-RETURN the "no billing rows yet"
 * message, which made the note editor unreachable in exactly the case it exists
 * for — bills only appear after a slot locks, so a pre-lock "he'll pay Friday"
 * slot had no route in at all. The message is now one block among others rather
 * than a gate, and the editor renders with zero bills and zero payments.
 */
const SlotExpandedRow = ({
  billingSchedules,
  currencyCode,
  offlinePayments = [],
  onVoidOffline,
  voidingBatchId,
  onVerifyOffline,
  verifyingBatchId,
  slotId,
  playerRegistrationId = null,
  note = null,
  onSaveNote,
  savingNote = false,
  onRemoveAddedInvoice,
  removingBillUid = null,
  removeBlockedReason,
}: SlotExpandedRowProps) => {
  const hasDetail = billingSchedules.length > 0 || offlinePayments.length > 0
  const showNoteEditor = Boolean(
    onSaveNote && slotId && playerRegistrationId !== null
  )

  const canRemoveAny = Boolean(
    onRemoveAddedInvoice && billingSchedules.some((row) => row.can_remove)
  )

  const columns = [
    {
      title: balanceText('common:balanceColType').toUpperCase(),
      key: 'type',
      render: (_: unknown, row: BalanceBillingSchedule) => (
        <span css={typeCellStyle}>
          {billingTypeLabel(row.payment_type)}
          {/* SN-8310 — what the rep said the added charge is for; the player
              sees the same words on their invoice. */}
          {row.payment_type === 'additional' && row.description ? (
            <span
              css={descriptionNoteStyle}
              data-testid={`billing-row-description-${row.uid}`}
            >
              {row.description}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      title: balanceText('common:balanceColDue').toUpperCase(),
      key: 'due',
      render: (_: unknown, row: BalanceBillingSchedule) => {
        if (!row.due_date) {
          return '—'
        }
        const overdue = !row.paid && isPastDueDay(row.due_date)
        return (
          <span
            css={
              overdue
                ? css`
                    color: ${colors.ERROR_200};
                  `
                : undefined
            }
          >
            {formatShortDate(row.due_date)}
          </span>
        )
      },
    },
    {
      title: balanceText('common:balanceColAmount').toUpperCase(),
      key: 'amount',
      align: 'right' as const,
      render: (_: unknown, row: BalanceBillingSchedule) => {
        /*
         * The amount is the invoice TOTAL, tax included. Its tax is the invoice's
         * own stored figure — never re-derived from a rate, because invoices
         * issued before tax moved on top keep the tax they were issued with.
         */
        const taxMinor = dollarsToMinor(Number(row.taxtotal) || 0)

        return (
          <span css={amountCellStyle}>
            <Price
              amount={dollarsToMinor(row.total)}
              currency={currencyCode}
              css={css`
                font-variant-numeric: tabular-nums;
              `}
            />
            {taxMinor > 0 ? (
              <span
                css={taxNoteStyle}
                data-testid={`billing-row-tax-${row.uid}`}
              >
                {balanceText('common:balanceTaxIncluded', {
                  amount: formatCurrency({
                    amount: taxMinor,
                    currencyCode,
                  }),
                })}
              </span>
            ) : null}
          </span>
        )
      },
    },
    {
      title: balanceText('common:balanceColState').toUpperCase(),
      key: 'state',
      render: (_: unknown, row: BalanceBillingSchedule) => {
        const state = rowState(row)
        return <StatusDotLabel dotColor={state.dotColor} label={state.label} />
      },
    },
    // Only when a row can actually be removed, so the table keeps its width otherwise.
    ...(canRemoveAny
      ? [
          {
            title: '',
            key: 'actions',
            align: 'right' as const,
            render: (_: unknown, row: BalanceBillingSchedule) => {
              if (!row.can_remove || !onRemoveAddedInvoice) {
                return null
              }
              // Sep 23 (D11) — the payer of their own line: shown, never actionable.
              if (removeBlockedReason) {
                return (
                  <Tooltip title={removeBlockedReason}>
                    {/* antd: a disabled button swallows mouse events — the span
                        receives the hover and the focus instead. */}
                    <span
                      tabIndex={0}
                      aria-label={`remove-added-invoice-blocked-${row.uid}`}
                    >
                      <InlineTextButton
                        tone='danger'
                        size='small'
                        disabled
                        text={balanceLabel('common:balanceRemoveAddedInvoice')}
                      />
                    </span>
                  </Tooltip>
                )
              }

              return (
                <InlineTextButton
                  tone='danger'
                  size='small'
                  isLoading={removingBillUid === row.uid}
                  onClick={() => onRemoveAddedInvoice(row)}
                  aria-label={`remove-added-invoice-${row.uid}`}
                  text={balanceLabel('common:balanceRemoveAddedInvoice')}
                />
              )
            },
          },
        ]
      : []),
  ]

  return (
    <div
      /**
       * C-5 moved this out of an inline expander and into a Drawer, but it kept
       * the expander's 46px indent and left rule — inside a Drawer that reads as
       * a stray vertical line down the panel and wastes a third of the width.
       * Full width, and the content sits on raised panels like the sheet and the
       * KPI cards.
       */
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        /*
           Top-aligned (Inon, Aug 12: "put payments just at the top"). Centring
           it in the drawer was tried and rejected — this is a panel you read
           top-down, and a table floating in the middle of a tall drawer reads
           as misplaced rather than as balanced.
        */
      `}
    >
      {/* Kept above the editor, exactly as before — a slot with nothing billed yet
          still has to say so; it just no longer suppresses everything below it. */}
      {!hasDetail ? (
        <div
          css={css`
            color: ${colors.NEUTRAL_100};
          `}
        >
          {balanceText('common:balanceNoBillingRows')}
        </div>
      ) : null}

      {billingSchedules.length > 0 ? (
        <div css={panelStyle}>
          <Table<BalanceBillingSchedule>
            columns={columns}
            dataSource={billingSchedules}
            rowKey='uid'
            pagination={false}
            size='small'
            css={css`
              .ant-table {
                background: transparent !important;
              }
              .ant-table-thead > tr > th {
                background: transparent !important;
                color: ${colors.NEUTRAL_70} !important;
                font-size: 11px;
                letter-spacing: 0.6px;
                text-transform: uppercase;
                padding: 6px 12px !important;
                border-bottom: 1px solid ${colors.SECONDARY_100} !important;
              }
              .ant-table-tbody > tr > td {
                font-size: 13px;
                padding: 8px 12px !important;
                border-bottom: 1px solid ${colors.SECONDARY_200} !important;
              }
              .ant-table-tbody > tr:last-child > td {
                border-bottom: none !important;
              }
              .ant-table-tbody > tr:hover > td {
                background: ${colors.SECONDARY_200} !important;
              }
            `}
          />
        </div>
      ) : null}

      {offlinePayments.length > 0 ? (
        <div
          data-testid='offline-batches'
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <span css={sectionTitleStyle}>
            {balanceText('common:balanceOfflinePaymentsHeading')}
          </span>
          {offlinePayments.map((batch) => (
            <OfflineBatchRow
              key={batch.id}
              batch={batch}
              currencyCode={currencyCode}
              onVoidOffline={onVoidOffline}
              voidingBatchId={voidingBatchId}
              onVerifyOffline={onVerifyOffline}
              verifyingBatchId={verifyingBatchId}
            />
          ))}
        </div>
      ) : null}

      {/* The key is the contract: a reassignment under an open drawer remounts the
          editor, so an unsaved draft about player A can never be saved against B. */}
      {showNoteEditor ? (
        <SlotNoteEditor
          key={`${slotId}:${playerRegistrationId}`}
          note={note}
          playerRegistrationId={playerRegistrationId as string}
          onSave={onSaveNote as NonNullable<SlotExpandedRowProps['onSaveNote']>}
          saving={savingNote}
        />
      ) : null}
    </div>
  )
}

export default SlotExpandedRow

const amountCellStyle = css`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
`

const taxNoteStyle = css`
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_70};
  white-space: nowrap;
`

const typeCellStyle = css`
  display: inline-flex;
  flex-direction: column;
`

/** Wraps rather than truncates: it is the rep's own words about a charge. */
const descriptionNoteStyle = css`
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_70};
  overflow-wrap: anywhere;
`
